// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0
import { useState, useLayoutEffect, useCallback, useEffect, createContext, useMemo } from "react";
import { useAppLayoutContext } from "../internal/context/app-layout-context";
import { useMobile } from "../internal/hooks/use-mobile";
import { findUpUntil, supportsStickyPosition } from "../internal/utils/dom";
import { getOverflowParents } from "../internal/utils/scrollable-containers";
import { useVisualRefresh } from "../internal/hooks/use-visual-mode";
import customCssProps from "../internal/generated/custom-css-properties";
export var StickyHeaderContext = /*#__PURE__*/ createContext({
    isStuck: false
});
export var useStickyHeader = function(rootRef, headerRef, __stickyHeader, __stickyOffset) {
    var currentRootRef = rootRef.current;
    var currentHeaderRef = headerRef.current;
    var totalBorder = useMemo(function() {
        var containerRootBorder = currentRootRef ? parseInt(getComputedStyle(currentRootRef).getPropertyValue("border-top-width"), 10) : 0;
        var headerBorder = currentHeaderRef ? parseInt(getComputedStyle(currentHeaderRef).getPropertyValue("border-top-width"), 10) : 0;
        return containerRootBorder + headerBorder;
    }, [
        currentRootRef,
        currentHeaderRef
    ]);
    // We reach into AppLayoutContext in case sticky header needs to be offset down by the height
    // of other sticky elements positioned on top of the view.
    var stickyOffsetTop = useAppLayoutContext().stickyOffsetTop;
    var isSticky = useSupportsStickyHeader() && !!__stickyHeader;
    var isRefresh = useVisualRefresh();
    // If it has overflow parents inside the app layout, we shouldn't apply a sticky offset.
    var _a = useState(false), hasInnerOverflowParents = _a[0], setHasInnerOverflowParents = _a[1];
    var _b = useState(false), isStuck = _b[0], setIsStuck = _b[1];
    useLayoutEffect(function() {
        if (rootRef.current) {
            var overflowParents = getOverflowParents(rootRef.current);
            var mainElement = findUpUntil(rootRef.current, function(elem) {
                return elem.tagName === "MAIN";
            });
            // In both versions of the app layout, the scrolling element for disableBodyScroll
            // is the <main>. If the closest overflow parent is also the closest <main> and we have
            // offset values, it's safe to assume that it's the app layout scroll root and we
            // should stop there.
            setHasInnerOverflowParents(overflowParents.length > 0 && overflowParents[0] !== mainElement);
        }
    }, [
        rootRef
    ]);
    var effectiveStickyOffset = __stickyOffset !== null && __stickyOffset !== void 0 ? __stickyOffset : hasInnerOverflowParents ? 0 : stickyOffsetTop;
    /**
     * The AppLayout refactor removed the need for passing the sticky offset in px all the time through the
     * AppLayoutDomContext provider because that information already exists on the DOM in a custom property
     * on the Layout subcomponent. Thus, if the Container header is sticky, we are in Visual Refresh and use
     * body scroll then we will use that property. When a component is used outside AppLayout, we fall back
     * to the default offset calculated in AppLayoutDomContext.
     */ var computedOffset = "".concat(effectiveStickyOffset - totalBorder, "px");
    if (isRefresh && !hasInnerOverflowParents) {
        computedOffset = "var(".concat(customCssProps.offsetTopWithNotifications, ", ").concat(computedOffset, ")");
    }
    var stickyStyles = isSticky ? {
        style: {
            top: computedOffset
        }
    } : {};
    // "stuck" state, when the header has moved from its original posititon has a
    // box-shadow, applied here by a "header-stuck" className
    var checkIfStuck = useCallback(function() {
        if (rootRef.current && headerRef.current) {
            var rootTop = rootRef.current.getBoundingClientRect().top;
            var headerTop = headerRef.current.getBoundingClientRect().top;
            if (rootTop + totalBorder < headerTop) {
                setIsStuck(true);
            } else {
                setIsStuck(false);
            }
        }
    }, [
        rootRef,
        headerRef,
        totalBorder
    ]);
    useEffect(function() {
        if (isSticky) {
            window.addEventListener("scroll", checkIfStuck, true);
            window.addEventListener("resize", checkIfStuck);
            return function() {
                window.removeEventListener("scroll", checkIfStuck, true);
                window.removeEventListener("resize", checkIfStuck);
            };
        }
    }, [
        isSticky,
        checkIfStuck
    ]);
    return {
        isSticky: isSticky,
        isStuck: isStuck,
        stickyStyles: stickyStyles
    };
};
export function useSupportsStickyHeader() {
    var isMobile = useMobile();
    return supportsStickyPosition() && !isMobile;
} //# sourceMappingURL=use-sticky-header.js.map
